import { type ReactNode } from 'react';
import { Download } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { FieldProse } from './DossierPrimitives';
import { UkDossierHeader } from './UkDossierHeader';
import type { DossierResponse } from '@/features/dossier/api';

/** Renders the one UKCA IVD Technical Documentation 6-chapter IMDRF-ToC
 *  document from a completed GB IVD dossier run (UK MDR 2002 Part IV,
 *  retained IVDD). GB IVD is single-document (like GB device and Canada MDL).
 *
 *  Chapter differences vs UkDossierView (device):
 *    ch3: "analytical_performance" (IVD) vs "nonclinical_evidence" (device)
 *    ch4: "clinical_performance" (IVD) vs "clinical_evidence" (device)
 *
 *  All other chapters (ch1/ch2/ch5/ch6) share the same keys as the device view.
 */
const CHAPTERS: { key: string; title: string }[] = [
  { key: 'ch1_regional_administrative', title: 'Regional Administrative' },
  { key: 'ch2_submission_context', title: 'Submission Context' },
  { key: 'ch3_analytical_performance', title: 'Analytical Performance' },
  { key: 'ch4_clinical_performance', title: 'Clinical Performance / Scientific Validity' },
  { key: 'ch5_labelling', title: 'Labelling' },
  { key: 'ch6_qms', title: 'Quality Management System' },
];

function labelize(k: string): string {
  return k.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

/** UK IVD–specific section wrapper: heading uses "Chapter N — Title" to match
 *  the PDF (uk_gb_ivd_renderer titles chapters). */
function ChapterSection({ num, title, children }: { num: number; title: string; children: ReactNode }) {
  return (
    <section className="bg-white border border-[#e0e0e0] rounded-xl p-[22px] text-[14px] text-ink leading-[1.55]">
      <h3 className="text-[17px] font-bold text-navy mt-0 mb-3 pb-1 border-b border-line">
        Chapter {num} — {title}
      </h3>
      <div>{children}</div>
    </section>
  );
}

export function UkIvdDossierView({
  response,
  runId,
}: {
  response: DossierResponse;
  /** Run id for the PDF download link. Optional so the component still renders
   *  (read-only) without a run context (e.g. tests). */
  runId?: number;
}) {
  const dossier = (response?.dossier ?? {}) as Record<string, unknown>;

  return (
    <div className="space-y-8">
      {/* UK MHRA IVD identity header (reuses UkDossierHeader — same ch1 fields). */}
      <UkDossierHeader response={response} />
      {/* Title-row + Download PDF — mirrors the device / Canada / US / EU renderers. */}
      <div className="flex items-center justify-between gap-3">
        <h2 className="text-[22px] font-bold text-heading -tracking-[0.2px] m-0">
          UKCA IVD Technical Documentation (UK MDR 2002 Part IV)
        </h2>
        {runId != null && (
          <Button variant="brand" size="sm" asChild>
            <a
              href={`/api/agents/dossier/runs/${runId}/pdf`}
              target="_blank"
              rel="noopener"
              title="Download as PDF"
            >
              <Download className="size-3.5" /> Download PDF
            </a>
          </Button>
        )}
      </div>
      {CHAPTERS.map((ch, i) => {
        const data = (dossier[ch.key] ?? {}) as Record<string, unknown>;
        const entries = Object.entries(data).filter(([, v]) => v != null && v !== '');
        return (
          <ChapterSection key={ch.key} num={i + 1} title={ch.title}>
            {entries.length === 0 ? (
              <p className="text-[13.5px] text-muted italic m-0">Not provided.</p>
            ) : (
              entries.map(([k, v]) => (
                <FieldProse key={k} label={`${labelize(k)}:`} body={String(v)} fallbackHint={labelize(k)} />
              ))
            )}
          </ChapterSection>
        );
      })}
    </div>
  );
}
